<template>
  <BContentBox v-loading="loading">
    <template #content>
      <div class="content-container">
        <div class="section">
          <div class="title">
            {{ $t('followUpPromptSetting.nextActionDefaultDateTime.title') }}
          </div>
          <div class="explain">
            {{ $t('followUpPromptSetting.nextActionDefaultDateTime.description') }}
          </div>
          <form @submit.prevent="handleSaveNextActionSuggestionDefaultSettingClick">
            <div
              class="time-items"
            >
              <BListItem class="mb-300">
                <template #header>
                  <label for="dateInterval">{{ $t('sequence.step.dateInterval') }}</label>
                </template>
                <BInput
                  v-model="dateInterval"
                  v-model:valid="isDateIntervalValid"
                  input-id="dateInterval"
                  type="number"
                  :max="365"
                  :min="0"
                  validation
                  required
                  needs-parse-int
                />
              </BListItem>
              <BListItem class="mb-300">
                <template #header>
                  <label>{{ $t('sequence.step.reservedTime') }}</label>
                </template>
                <BTimePicker
                  v-model="reservedTime"
                  input-id="reservedTime"
                  value-format="HH:mm"
                  format="HH:mm"
                  required
                />
              </BListItem>
            </div>
            <BLayout class="mb-800">
              <BBtn
                type="primary"
                :disabled="loading"
                btn-type="submit"
              >
                {{ $t('general.save.text') }}
              </BBtn>
            </BLayout>
          </form>
          <div class="title">
            {{ $t('followUpPromptSetting.hearingItems.title') }}
          </div>
          <div class="explain">
            {{ $t('followUpPromptSetting.hearingItems.description') }}
          </div>
          <div class="example">
            <BExpansionPanel>
              <template #header>
                <div class="example-title">
                  {{ $t('followUpPromptSetting.hearingItems.example.title') }}
                </div>
              </template>
              <div class="example-content">
                {{ $t('followUpPromptSetting.hearingItems.example.description') }}
              </div>
            </BExpansionPanel>
          </div>
          <div class="mb-300">
            <BTextarea
              v-model="hearingItems"
              required
              class="form"
              :autosize="{ minRows: 12, maxRows: 30 }"
            />
          </div>
        </div>
        <div class="section">
          <div class="title">
            {{ $t('followUpPromptSetting.hearingItemDescriptions.title') }}
          </div>
          <div class="explain">
            {{ $t('followUpPromptSetting.hearingItemDescriptions.description') }}
          </div>
          <div class="example">
            <BExpansionPanel>
              <template #header>
                <div class="example-title">
                  {{ $t('followUpPromptSetting.hearingItemDescriptions.example.title') }}
                </div>
              </template>
              <div class="example-content">
                {{ $t('followUpPromptSetting.hearingItemDescriptions.example.description') }}
              </div>
            </BExpansionPanel>
          </div>
          <div class="mb-300">
            <BTextarea
              v-model="hearingItemDescriptions"
              required
              class="form"
              :autosize="{ minRows: 12, maxRows: 30 }"
            />
          </div>
        </div>
        <div class="section">
          <div class="title">
            {{ $t('followUpPromptSetting.judgmentCondition.title') }}
          </div>
          <div class="explain">
            {{ $t('followUpPromptSetting.judgmentCondition.description') }}
          </div>
          <div class="example">
            <BExpansionPanel>
              <template #header>
                <div class="example-title">
                  {{ $t('followUpPromptSetting.judgmentCondition.example.title') }}
                </div>
              </template>
              <div class="example-content">
                {{ $t('followUpPromptSetting.judgmentCondition.example.description') }}
              </div>
            </BExpansionPanel>
          </div>
          <div class="mb-300">
            <BTextarea
              v-model="judgmentCondition"
              required
              class="form"
              :autosize="{ minRows: 12, maxRows: 30 }"
            />
          </div>
        </div>

        <BLayout>
          <BBtn
            type="primary"
            :disabled="loading"
            @click="handleSaveClick"
          >
            {{ $t('general.save.text') }}
          </BBtn>
        </BLayout>
      </div>
    </template>
  </BContentBox>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { NextActionSuggestionDefaultSettingApiService } from '@/api/user/resources/next_action_suggestion_default_setting';
import { PromptSettingApiService } from '@/api/user/resources/prompt_setting';
import { useWait } from '@/composable/vue-wait';

const dateInterval = ref<number>();
const reservedTime = ref<string>();
const hearingItems = ref<string>();
const hearingItemDescriptions = ref<string>();
const judgmentCondition = ref<string>();

const isDateIntervalValid = ref(false);

const { doActionWithWait, wait } = useWait();

const loading = computed(() =>
  wait.is([
    'fetchNextActionSuggestionDefaultSettingWait',
    'fetchPromptSettingWait',
    'putNextActionSuggestionDefaultSettingWait',
    'putPromptSettingWait',
  ]),
);

const fetchNextActionSuggestionDefaultSetting = () => {
  doActionWithWait('fetchNextActionSuggestionDefaultSettingWait', async () => {
    const api = new NextActionSuggestionDefaultSettingApiService();
    const { data } = await api.getNextActionSuggestionDefaultSetting({
      errorHandlers: {
        404: () => undefined,
      },
    });
    if (data?.nextActionSuggestionDefaultSetting) {
      dateInterval.value = data.nextActionSuggestionDefaultSetting.dateInterval;
      reservedTime.value = data.nextActionSuggestionDefaultSetting.reservedTime;
    }
  });
};

const fetchPromptSetting = () => {
  doActionWithWait('fetchPromptSettingWait', async () => {
    const api = new PromptSettingApiService();
    const { data } = await api.getFollowUpPromptSetting({
      errorHandlers: {
        404: () => undefined,
      },
    });
    hearingItems.value = data.promptSetting.hearingItems;
    hearingItemDescriptions.value = data.promptSetting.hearingItemDescriptions;
    judgmentCondition.value = data.promptSetting.judgmentCondition;
  });
};

const handleSaveNextActionSuggestionDefaultSettingClick = async () => {
  doActionWithWait('putNextActionSuggestionDefaultSettingWait', async () => {
    const nextActionApi = new NextActionSuggestionDefaultSettingApiService();
    await nextActionApi.putNextActionSuggestionDefaultSetting({
      request: {
        nextActionSuggestionDefaultSettingBody: {
          nextActionSuggestionDefaultSetting: {
            dateInterval: dateInterval.value,
            reservedTime: reservedTime.value,
          },
        },
      },
    });
  });
};

const handleSaveClick = async () => {
  doActionWithWait('putPromptSettingWait', async () => {
    const api = new PromptSettingApiService();
    await api.putFollowUpPromptSetting({
      request: {
        getFollowUpPromptSetting200Response: {
          promptSetting: {
            hearingItems: hearingItems.value,
            hearingItemDescriptions: hearingItemDescriptions.value,
            judgmentCondition: judgmentCondition.value,
          },
        },
      },
    });
  });
};

onBeforeMount(() => {
  fetchNextActionSuggestionDefaultSetting();
  fetchPromptSetting();
});
</script>

<style lang="scss" scoped>
.content-container {
  max-width: 900px;
}
.title {
  font-size: $fontsize-400;
  font-weight: bold;
  margin-bottom: $basespace-150;
}
.explain {
  color: $textcolor-light;
  white-space: pre-wrap;
  margin-bottom: $basespace-400;
}
.time-items {
  display: flex;
  gap: 10px;
  max-width: 480px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.example {
  margin-bottom: $basespace-200;
}
.example :deep(*){
  color: $textcolor-light;
}
.form {
  width: 100%;
}
.example-content {
  @include m-break-word-pre-wrap
}
</style>
